<template>
  <k-dialog v-if="open" class="cc-grid-dialog" :title="''" @close="onDialogClose">
    <div class="ccgd-shell">
      <GridDialogHeader
        :title="title"
        :results="resultCount"
        :view-mode="viewMode"
        :send-noti-button="sendNotiButton"
        :sending="sending"
        @set-view="viewMode = $event"
        @close="close"
        @send-task-notification="onSendTaskNotification"
      />

      <GridDialogToolbar
        :search-word="searchWord"
        :search-field="searchField"
        :search-field-options="searchFieldOptions"
        :view-mode="viewMode"
        :all-selected="allSelectedInList"
        :incomplete-app-switch="incompleteAppSwitch"
        :incomplete-app-switch-checked="incompleteAppSwitchChecked"
        @search="onSearchInput"
        @search-field-change="onSearchFieldChange"
        @clear-search="clearSearch"
        @toggle-select-all="toggleSelectAllList"
        @toggle-incomplete-app-switch="$emit('toggle-incomplete-app-switch')"
        @export="exportExcel"
      />

      <!-- Advanced filters (loan #, borrower, task age, est. close) -->
      <GridDialogFilters
        v-if="enableFilters"
        v-model="advFilters"
        :available="filterAvailability"
        :active-count="activeFilterCount"
        :shown-tasks="shownItemCount"
        :total-tasks="totalItemCount"
        @clear="clearAdvancedFilters"
      />

      <div class="ccgd-expand-pref-row" v-if="viewMode === 'list'">
        <label class="ccgd-expand-pref">
          <input
            type="checkbox"
            :checked="alwaysExpandRows"
            @change="onToggleAlwaysExpandRows"
          />
          <span>Always expand all rows</span>
        </label>
      </div>

      <div class="ccgd-grid-wrap">
        <div v-if="loading" class="ccgd-loading-state">
          <i class="fa-solid fa-spinner-third fa-spin ccgd-loading-state__icon"></i>
          <div class="ccgd-loading-state__text">{{ loadingLabel }}</div>
        </div>

        <template v-else>
          <GridDialogList
            v-if="viewMode === 'list'"
            class="ccgd-listview"
            :data-items="pagedListData"
            :selected-field="selectedField"
            :selected-ids="selectedIds"
            :dialog-type="resolvedDialogType"
            :auto-expand-groups="isSearching || alwaysExpandRows"
            @row-click="onListRowClick"
            @toggle-selected="toggleRowSelected"
            @complete-task="onCompleteTask"
            @notify-borrower="onSingleNotifyRequested"
            @open-app="$emit('open-app', $event)"
          >
            <template #actionsCell="slotProps">
              <slot name="actionsCell" v-bind="slotProps" />
            </template>
          </GridDialogList>

          <CommandCenterTable
            v-else
            :title="title"
            
            :columns="effectiveColumns"
            :data="filteredGridData"
            :selectable="true"
            :selected-field="selectedField"
            :all-selected="allSelectedInList"
            :active-sort="dataState.sort"
            :default-page-size="25"
            :max-page-size="100"
            empty-text="No records found."
            @row-click="onListRowClick"
            @toggle-selected="toggleRowSelected"
            @toggle-all="toggleSelectAllGrid"
            @sort="onHandleSort"
          >
            <template #titleCell="{ dataItem }">
              <div class="ccgd-grid-title">
                <div class="ccgd-grid-title-primary">{{ dataItem.__title }}</div>
                <div class="ccgd-grid-title-sub">
                  <span v-if="dataItem.__sub">{{ dataItem.__sub }}</span>
                  <span v-if="dataItem.__key" class="ccgd-grid-dim"> • {{ dataItem.__key }}</span>
                </div>
              </div>
            </template>

            <template #typeCell="{ dataItem }">
              <span class="ccgd-type-pill" :data-type="dataItem.__type">
                {{ typeLabel(dataItem.__type) }}
              </span>
            </template>

            <template #statusCell="{ dataItem }">
              <span class="ccgd-status-pill" :data-status="(dataItem.__status || '').toLowerCase()">
                {{ dataItem.__status || "—" }}
              </span>
            </template>

            <template #dateCell="{ dataItem }">
              <span class="ccgd-grid-date">{{ formatShortDate(dataItem.__date) }}</span>
            </template>

            <template #followUpCell="{ dataItem }">
              <span class="ccgd-grid-date">{{ formatShortDate(dataItem.__followUpDate) }}</span>
            </template>

            <template #assignedToCell="{ dataItem }">
              <span class="ccgd-grid-assigned">{{ dataItem.__assignedTo || "—" }}</span>
            </template>

            <template #actionsCell="{ dataItem }">
              <slot name="actionsCell" :data-item="dataItem" />
            </template>
          </CommandCenterTable>
        </template>
      </div>

      <div
        v-if="!loading && viewMode === 'list' && totalListItems > 0"
        class="ccgd-list-pager"
      >
        <div class="ccgd-list-pager__left">
          <span class="ccgd-list-pager__summary">
            Showing {{ listRangeStart }} to {{ listRangeEnd }} of {{ totalListItems }}
          </span>

          <label class="ccgd-list-pager__size">
            <span>Rows</span>
            <select :value="listPage.take" @change="onListPageSizeChange">
              <option v-for="size in listPageSizeOptions" :key="size" :value="size">
                {{ size }}
              </option>
            </select>
          </label>
        </div>

        <div class="ccgd-list-pager__right">
          <button
            type="button"
            class="ccgd-page-btn"
            :disabled="listPageNumber === 1"
            @click="goToListPage(1)"
          >
            First
          </button>

          <button
            type="button"
            class="ccgd-page-btn"
            :disabled="listPageNumber === 1"
            @click="goToListPage(listPageNumber - 1)"
          >
            Prev
          </button>

          <span class="ccgd-list-pager__page">
            Page {{ listPageNumber }} of {{ totalListPages }}
          </span>

          <button
            type="button"
            class="ccgd-page-btn"
            :disabled="listPageNumber === totalListPages"
            @click="goToListPage(listPageNumber + 1)"
          >
            Next
          </button>

          <button
            type="button"
            class="ccgd-page-btn"
            :disabled="listPageNumber === totalListPages"
            @click="goToListPage(totalListPages)"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  </k-dialog>

  <ConfirmDialog
    class="confirm-dialog"
    v-if="showConfirmDialog"
    :title="confirmTitle"
    :msg="confirmMsg"
    :note="confirmNote"
    @confirm="onActionConfirmed"
    @cancel="toggleConfirmDialog"
  />

  <ErrorDialog
    class="error-dialog"
    v-if="showErrorDialog"
    :open="true"
    :title="errorTitle"
    :msg="errorMsg"
    @close="closeErrorDialog"
  />

  <!-- Work Item #9107: no host dashboard listens for notification-success, so the dialog shows its own
       confirmation using the same cc-toast pattern as the Command Center dashboards. -->
  <transition name="cc-toast">
    <div v-if="sendToast.show" class="cc-toast" :class="`cc-toast--${sendToast.type}`" role="status" aria-live="polite">
      <i class="fa-solid" :class="sendToast.type === 'success' ? 'fa-circle-check' : 'fa-circle-xmark'"></i>
      {{ sendToast.message }}
    </div>
  </transition>
</template>

<script>
import auth from "@/authConfig.js"
import apiService from "@/api/api.service.js"
import { Dialog } from "@progress/kendo-vue-dialogs"
import { saveExcel } from "@progress/kendo-vue-excel-export"

import GridDialogHeader from "@/components/commandCenter/GridDialogHeader.vue"
import GridDialogToolbar from "@/components/commandCenter/GridDialogToolbar.vue"
import GridDialogFilters from "@/components/commandCenter/GridDialogFilters.vue"
import GridDialogList from "@/components/commandCenter/GridDialogList.vue"
import ConfirmDialog from "@/components/commandCenter/ConfirmDialog.vue"
import ErrorDialog from "@/components/commandCenter/ErrorDialog.vue"
import CommandCenterTable from "@/components/commandCenter/CommandCenterTable.vue"

const EXPAND_PREF_KEY = "cc_griddialog_always_expand_rows"

const DEFAULT_ADV_FILTERS = () => ({
  loan: "",
  borrower: "",
  createdPreset: "all",
  createdFrom: "",
  createdTo: "",
  closeFrom: "",
  closeTo: "",
  includeNoClose: true,
  sortBy: "default"
})

export default {
  name: "GridDialog",
  components: {
    "k-dialog": Dialog,
    GridDialogHeader,
    GridDialogToolbar,
    GridDialogFilters,
    GridDialogList,
    ConfirmDialog,
    ErrorDialog,
    CommandCenterTable
  },
  props: {
    open: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    loadingLabel: { type: String, default: "Loading..." },
    title: { type: String, default: "" },
    dialogType: { type: String, default: "" },
    columns: { type: Array, default: () => [] },
    dataItems: { type: Array, default: () => [] },
    filter: { type: Object, default: () => ({ logic: "and", filters: [] }) },
    sort: { type: Array, default: () => [] },
    selectedField: { type: String, default: "selected" },
    sendNotiButton: { type: Boolean, default: false },
    incompleteAppSwitch: { type: Boolean, default: false },
    incompleteAppSwitchChecked: { type: Boolean, default: false },
    // Advanced filter bar (loan #, borrower, task age, est. close, sort). Pass :enable-filters="false" to hide.
    enableFilters: { type: Boolean, default: true }
  },
  emits: [
    "close",
    "row-click",
    "selection-change",
    "header-selection-change",
    "export-excel",
    "complete-task",
    "toggle-incomplete-app-switch",
    "open-app",
    "notification-success"
  ],
  data() {
    return {
      selectedIds: new Set(),
      viewMode: "list",
      localData: [],
      originalData: [],
      searchWord: "",
      searchField: "all",
      advFilters: DEFAULT_ADV_FILTERS(),
      dataState: {
        skip: 0,
        take: 100000,
        filter: this.filter,
        sort: this.sort
      },
      listPage: {
        skip: 0,
        take: 25
      },
      listPageSizeOptions: [10, 25, 50, 100],
      expandedCardIds: new Set(),
      alwaysExpandRows: false,
      singleNotifyTask: null,
      showConfirmDialog: false,
      confirmTitle: "Please Confirm",
      confirmMsg: "",
      confirmNote: "",
      confirmAction: "",
      showErrorDialog: false,
      errorTitle: "",
      errorMsg: "",
      sending: false,
      sendToast: { show: false, message: "", type: "success" }
    }
  },
  computed: {
    resolvedDialogType() {
      const dt = (this.dialogType || "").toString().trim().toLowerCase()
      const isValid = dt && dt !== "unknown" && dt !== "null" && dt !== "undefined"

      if (isValid) {
        return this.dialogType
      }

      const raw = (this.title || "").toLowerCase().replace(/\s+/g, " ").trim()

      if (!raw) return "unknown"
      if (raw.includes("borrower")) return "borrowerTask"
      if (raw.includes("ll") && raw.includes("task")) return "borrowerTask"
      if (raw.includes("loan level") && raw.includes("task")) return "borrowerTask"
      if (raw.includes("internal")) return "internalTask"
      if (raw.includes("application")) return "application"
      if (raw.includes("loan") || raw.includes("pipeline")) return "loan"

      return "unknown"
    },
    isSearching() {
      return !!(this.searchWord || "").trim()
    },
    // Only offer a filter when at least one row actually carries that field
    filterAvailability() {
      const items = this.leafItems(this.originalData)
      return {
        loan: items.some((t) => !!this.leafLoan(t)),
        borrower: items.some((t) => !!this.leafBorrower(t)),
        created: items.some((t) => !!this.leafCreated(t)),
        close: items.some((t) => !!this.leafClose(t))
      }
    },
    activeFilterCount() {
      if (!this.enableFilters) return 0
      const f = this.advFilters
      let n = 0
      if ((f.loan || "").trim()) n++
      if ((f.borrower || "").trim()) n++
      if (f.createdPreset !== "all" && (f.createdPreset !== "custom" || f.createdFrom || f.createdTo)) n++
      if (f.closeFrom || f.closeTo) n++
      return n
    },
    totalItemCount() {
      return this.leafItems(this.originalData).length
    },
    shownItemCount() {
      return this.leafItems(this.localData).length
    },
    searchFieldOptions() {
      const base = [{ value: "all", text: "All Fields" }]

      if (this.resolvedDialogType === "borrowerTask") {
        return [
          ...base,
          { value: "__sub", text: "Borrower Name" },
          { value: "__key", text: "Loan Number" },
          { value: "__title", text: "Title" },
          { value: "__status", text: "Status" },
          { value: "__assignedTo", text: "Assigned To" },
          { value: "__date", text: "Date Created" }
        ]
      }

      if (this.resolvedDialogType === "internalTask") {
        return [
          ...base,
          { value: "__sub", text: "Borrower Name" },
          { value: "__key", text: "Loan Number" },
          { value: "__title", text: "Title" },
          { value: "__status", text: "Status" },
          { value: "__assignedTo", text: "Assigned To" },
          { value: "__date", text: "Date Created" },
          { value: "__followUpDate", text: "Follow Up Date" }
        ]
      }

      if (this.resolvedDialogType === "application") {
        return [
          ...base,
          { value: "__sub", text: "Borrower Name" },
          { value: "__key", text: "Application Id" },
          { value: "__status", text: "Status" },
          { value: "__assignedTo", text: "Loan Officer" },
          { value: "__date", text: "Application Date" }
        ]
      }

      if (this.resolvedDialogType === "loan") {
        return [
          ...base,
          { value: "__title", text: "Loan Name" },
          { value: "__sub", text: "Loan Number" },
          { value: "__key", text: "Loan Number" },
          { value: "__status", text: "Status" },
          { value: "__assignedTo", text: "Loan Officer" },
          { value: "__date", text: "Date" }
        ]
      }

      return [
        ...base,
        { value: "__title", text: "Title" },
        { value: "__sub", text: "Sub Text" },
        { value: "__key", text: "Key" },
        { value: "__status", text: "Status" },
        { value: "__assignedTo", text: "Assigned To" },
        { value: "__date", text: "Date" }
      ]
    },
    filteredData() {
      return (this.localData || []).map((item, idx) => {
        const row = { ...item }
        const id = row.id || row.task_id || row.__rowId || `list_${idx}`
        row.__rowId = id
        row[this.selectedField] = this.selectedIds.has(id)
        return row
      })
    },
    pagedListData() {
      const start = this.listPage.skip
      const end = start + this.listPage.take
      return this.filteredData.slice(start, end)
    },
    gridData() {
      return this.flattenForGrid(this.localData)
    },
    originalGridData() {
      return this.flattenForGrid(this.originalData)
    },
    filteredGridData() {
      const rows = this.gridData || []
      const set = this.selectedIds || new Set()

      return rows.map((r, idx) => {
        const id = r.id || r.task_id || r.__rowId || `grid_${idx}`
        return {
          ...r,
          __rowId: id,
          [this.selectedField]: !!(id && set.has(id))
        }
      })
    },
    resultCount() {
      return this.viewMode === "grid"
        ? this.filteredGridData.length
        : this.filteredData.length
    },
    totalListItems() {
      return this.filteredData.length
    },
    totalListPages() {
      return Math.max(1, Math.ceil(this.totalListItems / this.listPage.take))
    },
    listPageNumber() {
      return Math.floor(this.listPage.skip / this.listPage.take) + 1
    },
    listRangeStart() {
      if (!this.totalListItems) return 0
      return this.listPage.skip + 1
    },
    listRangeEnd() {
      if (!this.totalListItems) return 0
      return Math.min(this.listPage.skip + this.listPage.take, this.totalListItems)
    },
    visibleRows() {
      return this.viewMode === "grid" ? this.filteredGridData : this.filteredData
    },
    allSelectedInList() {
      const rows = this.viewMode === "grid" ? this.filteredGridData : this.pagedListData
      if (!rows.length) return false

      return rows.every((r, idx) => {
        const id = r.id || r.task_id || r.__rowId || `selected_${idx}`
        return this.selectedIds.has(id)
      })
    },
    effectiveColumns() {
      if (Array.isArray(this.columns) && this.columns.length) {
        return this.columns
      }

      if (this.resolvedDialogType === "internalTask") {
        return [
          { field: "__title", title: "Title", cell: "titleCell" },
          { field: "__type", title: "Type", cell: "typeCell", width: 140 },
          { field: "__status", title: "Status", cell: "statusCell", width: 140 },
          { field: "__date", title: "Date Created", cell: "dateCell", width: 140 },
          { field: "__followUpDate", title: "Follow Up", cell: "followUpCell", width: 140 },
          { field: "__assignedTo", title: "Assigned To", cell: "assignedToCell", width: 180 },
          { field: "actions", title: "Actions", cell: "actionsCell", width: 180 }
        ]
      }

      return [
        { field: "__title", title: "Title", cell: "titleCell" },
        { field: "__type", title: "Type", cell: "typeCell", width: 140 },
        { field: "__status", title: "Status", cell: "statusCell", width: 140 },
        { field: "__date", title: "Date", cell: "dateCell", width: 140 },
        { field: "__assignedTo", title: "Assigned To", cell: "assignedToCell", width: 180 },
        { field: "actions", title: "Actions", cell: "actionsCell", width: 180 }
      ]
    },
    isBorrowerTaskDialog() {
      // Work Item #9107: the Command Center role dashboards open borrower-task drilldowns as
      // "borrowerTaskGrouped"; the send path only accepted "borrowerTask", so Send Notifications did nothing.
      return this.resolvedDialogType === "borrowerTask" || this.resolvedDialogType === "borrowerTaskGrouped"
    },
    selectedTaskRows() {
      if (!this.isBorrowerTaskDialog) return []

      // Rows are stamped with the dialog's resolved type by normalizeRow(). __rowId is not used as a
      // fallback because it can be a synthetic "<loanNumber>_<index>" key, not a loan id the API accepts.
      return this.flattenForGrid(this.filteredData || [])
        .filter((r) => r.__type === this.resolvedDialogType)
        .filter((r) => {
          const id = r.id || r.task_id
          return id && this.selectedIds.has(id)
        })
    },
    filteredTaskIdsToSend() {
      // Row ids are loan ids and the API emails every borrower on the loan per call,
      // so dedupe to one call per loan (Classic did the same).
      return [...new Set(this.selectedTaskRows.map((r) => r.id || r.task_id))]
    },
    loansToNotify() {
      // Work Item #9107: signing tasks (eSign) go to the signing reminder endpoint; the existing task
      // notification skips signing tasks by design. A loan with both kinds selected gets both emails.
      const signing = new Set()
      const tasks = new Set()
      const loanNumbers = {}

      this.selectedTaskRows.forEach((r) => {
        const id = r.id || r.task_id
        loanNumbers[id] = r.__key || loanNumbers[id] || ""

        if (String(r.signingtask ?? r.signingTask ?? "").toLowerCase() === "true") signing.add(id)
        else tasks.add(id)
      })

      return { signingLoanIds: [...signing], taskLoanIds: [...tasks], loanNumbers }
    }
  },
  watch: {
    dataItems: {
      immediate: true,
      handler(val) {
        const arr = Array.isArray(val) ? val : []
        const forcedType = this.resolvedDialogType
        const normalized = arr.map((x) => this.normalizeRow({ ...x }, forcedType))
        this.localData = normalized
        this.originalData = normalized.map((x) => ({ ...x }))
        this.selectedIds = new Set()
        this.expandedCardIds = new Set()
        this.resetListPaging()
        this.ensureValidSearchField()
        this.applySearch()
      }
    },
    title() {
      this.advFilters = DEFAULT_ADV_FILTERS()
      const arr = Array.isArray(this.dataItems) ? this.dataItems : []
      const forcedType = this.resolvedDialogType
      const normalized = arr.map((x) => this.normalizeRow({ ...x }, forcedType))
      this.localData = normalized
      this.originalData = normalized.map((x) => ({ ...x }))
      this.selectedIds = new Set()
      this.expandedCardIds = new Set()
      this.resetListPaging()
      this.ensureValidSearchField()
      this.applySearch()
    },
    filter: {
      deep: true,
      handler(val) {
        this.dataState = { ...this.dataState, filter: val, skip: 0 }
      }
    },
    sort: {
      deep: true,
      handler(val) {
        this.dataState = { ...this.dataState, sort: val }
      }
    },
    viewMode() {
      this.resetListPaging()
      this.applySearch()
    },
    advFilters: {
      deep: true,
      handler() {
        if (this.enableFilters) this.applySearch()
      }
    }
  },
  mounted() {
    this.loadExpandPreference()
    this.ensureValidSearchField()
  },
  beforeUnmount() {
    clearTimeout(this.sendToastTimer)
  },
  methods: {
    onCompleteTask(task) {
  const id = task?.id || task?.task_id
  if (id) {
    this.localData = this.localData.filter(item => {
      // Handle flat rows
      if (item.id === id || item.task_id === id) return false
      // Handle grouped rows (loan groups with nested tasks)
      if (Array.isArray(item.tasks)) {
        item.tasks = item.tasks.filter(t => t.id !== id && t.task_id !== id)
        return item.tasks.length > 0
      }
      return true
    })
    this.originalData = this.originalData.filter(item => {
      if (item.id === id || item.task_id === id) return false
      if (Array.isArray(item.tasks)) {
        item.tasks = item.tasks.filter(t => t.id !== id && t.task_id !== id)
        return item.tasks.length > 0
      }
      return true
    })
  }
  this.$emit('complete-task', task)
},
    // ---- Advanced filters -------------------------------------------------
    // Rows are either loan groups ({ loanNumber, borrowers, tasks: [...] }) or flat rows.
    // Filters apply per task; a group is dropped once none of its tasks survive.
    leafItems(rows) {
      const out = []
      for (const r of rows || []) {
        const g = this.unwrap(r) || r
        if (Array.isArray(g?.tasks)) out.push(...g.tasks.map((t) => this.unwrap(t) || t))
        else out.push(g)
      }
      return out
    },
    leafLoan(t, group) {
      return String(t?.f364 || t?.loannumber || group?.loanNumber || "").trim()
    },
    leafBorrower(t, group) {
      const b = t?.borrower_name || t?.borrowername || t?.name ||
        (Array.isArray(group?.borrowers) ? group.borrowers.join(" ") : group?.borrowers) || ""
      return String(b).trim()
    },
    toDateOrNull(v) {
      v = this.unwrap(v)
      if (!v) return null
      if (v instanceof Date) return isNaN(v.getTime()) ? null : v
      const d = new Date(v)
      return isNaN(d.getTime()) ? null : d
    },
    leafCreated(t) {
      return this.toDateOrNull(t?.created || t?.datecreated || t?.createddate || t?.dateCreated)
    },
    leafClose(t) {
      return this.toDateOrNull(t?.cust17fv || t?.estclosingdate)
    },
    // <input type="date"> gives YYYY-MM-DD; treat as local-day bounds
    dayStart(ymd) {
      if (!ymd) return null
      const [y, m, d] = ymd.split("-").map(Number)
      return new Date(y, m - 1, d, 0, 0, 0, 0)
    },
    dayEnd(ymd) {
      if (!ymd) return null
      const [y, m, d] = ymd.split("-").map(Number)
      return new Date(y, m - 1, d, 23, 59, 59, 999)
    },
    createdBounds() {
      const f = this.advFilters
      const now = new Date()
      const daysAgo = (n) => {
        const d = new Date(now)
        d.setHours(0, 0, 0, 0)
        d.setDate(d.getDate() - n)
        return d
      }
      switch (f.createdPreset) {
        case "30":
        case "90":
        case "180":
        case "365":
          return { from: daysAgo(Number(f.createdPreset)), to: null }
        case "older365":
          return { from: null, to: daysAgo(365) }
        case "custom":
          return { from: this.dayStart(f.createdFrom), to: this.dayEnd(f.createdTo) }
        default:
          return { from: null, to: null }
      }
    },
    taskPassesFilters(t, group, ctx) {
      if (ctx.loan && !this.leafLoan(t, group).toLowerCase().includes(ctx.loan)) return false
      if (ctx.borrower && !this.leafBorrower(t, group).toLowerCase().includes(ctx.borrower)) return false

      if (ctx.created.from || ctx.created.to) {
        const c = this.leafCreated(t)
        if (!c) return false
        if (ctx.created.from && c < ctx.created.from) return false
        if (ctx.created.to && c > ctx.created.to) return false
      }

      if (ctx.closeFrom || ctx.closeTo) {
        const c = this.leafClose(t)
        if (!c) return ctx.includeNoClose
        if (ctx.closeFrom && c < ctx.closeFrom) return false
        if (ctx.closeTo && c > ctx.closeTo) return false
      }
      return true
    },
    applyAdvancedFilters(rows) {
      const f = this.advFilters
      const ctx = {
        loan: (f.loan || "").trim().toLowerCase(),
        borrower: (f.borrower || "").trim().toLowerCase(),
        created: this.createdBounds(),
        closeFrom: this.dayStart(f.closeFrom),
        closeTo: this.dayEnd(f.closeTo),
        includeNoClose: !!f.includeNoClose
      }

      const out = []
      for (const r of rows || []) {
        const g = this.unwrap(r) || r
        if (Array.isArray(g?.tasks)) {
          const tasks = g.tasks.filter((t) => this.taskPassesFilters(this.unwrap(t) || t, g, ctx))
          if (tasks.length) out.push({ ...r, tasks })
        } else if (this.taskPassesFilters(g, null, ctx)) {
          out.push(r)
        }
      }
      return this.sortAdvanced(out)
    },
    sortAdvanced(rows) {
      const by = this.advFilters.sortBy
      if (!by || by === "default") return rows

      const pick = by.startsWith("created") ? (t) => this.leafCreated(t) : (t) => this.leafClose(t)
      const asc = by.endsWith("Asc")
      // For groups: sort by the group's oldest/newest (or soonest/latest) task
      const keyOf = (r) => {
        const g = this.unwrap(r) || r
        const items = Array.isArray(g?.tasks) ? g.tasks.map((t) => this.unwrap(t) || t) : [g]
        const times = items.map(pick).filter(Boolean).map((d) => d.getTime())
        if (!times.length) return null
        return asc ? Math.min(...times) : Math.max(...times)
      }
      return rows
        .map((r) => ({ r, k: keyOf(r) }))
        .sort((a, b) => {
          if (a.k == null && b.k == null) return 0
          if (a.k == null) return 1 // blanks last
          if (b.k == null) return -1
          return asc ? a.k - b.k : b.k - a.k
        })
        .map((x) => x.r)
    },
    clearAdvancedFilters() {
      this.advFilters = DEFAULT_ADV_FILTERS()
    },
    ensureValidSearchField() {
      const valid = (this.searchFieldOptions || []).some((x) => x.value === this.searchField)
      if (!valid) this.searchField = "all"
    },
    resetListPaging() {
      this.listPage = {
        ...this.listPage,
        skip: 0
      }
    },
    clampListPage() {
      const maxSkip = Math.max(0, (this.totalListPages - 1) * this.listPage.take)
      if (this.listPage.skip > maxSkip) {
        this.listPage = {
          ...this.listPage,
          skip: maxSkip
        }
      }
    },
    goToListPage(pageNumber) {
      const page = Math.min(Math.max(1, pageNumber), this.totalListPages)
      this.listPage = {
        ...this.listPage,
        skip: (page - 1) * this.listPage.take
      }
    },
    onListPageSizeChange(e) {
      const nextTake = Number(e?.target?.value) || 25
      this.listPage = {
        skip: 0,
        take: nextTake
      }
    },
    loadExpandPreference() {
      try {
        const raw = localStorage.getItem(EXPAND_PREF_KEY)
        this.alwaysExpandRows = raw === "true"
      } catch {
        this.alwaysExpandRows = false
      }
    },
    saveExpandPreference() {
      try {
        localStorage.setItem(EXPAND_PREF_KEY, this.alwaysExpandRows ? "true" : "false")
      } catch {
      }
    },
    onToggleAlwaysExpandRows(e) {
      this.alwaysExpandRows = !!e?.target?.checked
      this.saveExpandPreference()
    },
    unwrap(v) {
      if (
        v &&
        typeof v === "object" &&
        v._custom &&
        Object.prototype.hasOwnProperty.call(v._custom, "value")
      ) {
        return v._custom.value
      }

      return v
    },
    flattenForGrid(rows) {
      const out = []

      ;(rows || []).forEach((row, idx) => {
        const item = this.unwrap(row) || row
        const groupTasks = Array.isArray(item?.tasks) ? item.tasks : null

        if (groupTasks && groupTasks.length) {
          groupTasks.forEach((task, taskIdx) => {
            const t = this.unwrap(task) || task
            const normalized = this.normalizeRow(
              {
                ...t,
                borrowername:
                  t?.borrowername ||
                  t?.borrower_name ||
                  (Array.isArray(item?.borrowers) ? item.borrowers.join(", ") : item?.borrowers || ""),
                borrower_name:
                  t?.borrower_name ||
                  t?.borrowername ||
                  (Array.isArray(item?.borrowers) ? item.borrowers.join(", ") : item?.borrowers || ""),
                loannumber: t?.loannumber || item?.loanNumber || item?.loannumber || item?.f364 || "",
                f364: t?.f364 || item?.loanNumber || item?.loannumber || item?.f364 || ""
              },
              this.resolvedDialogType
            )

            normalized.__rowId =
              t?.id ||
              t?.task_id ||
              `${item?.loanNumber || item?.loannumber || item?.f364 || "group"}_${taskIdx}`

            out.push(normalized)
          })

          return
        }

        const normalized = this.normalizeRow({ ...item }, this.resolvedDialogType)
        normalized.__rowId =
          item?.id ||
          item?.task_id ||
          item?.appid ||
          item?.loannumber ||
          item?.f364 ||
          `row_${idx}`

        out.push(normalized)
      })

      return out
    },
    toSearchText(value) {
      const seen = new Set()

      const walk = (v) => {
        v = this.unwrap(v)

        if (v == null) return ""
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v)
        if (v instanceof Date) return v.toISOString()
        if (Array.isArray(v)) return v.map(walk).join(" ")

        if (typeof v === "object") {
          if (seen.has(v)) return ""
          seen.add(v)

          return Object.keys(v)
            .map((k) => {
              if (k === "selected") return ""
              return walk(v[k])
            })
            .join(" ")
        }

        return ""
      }

      return walk(value)
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
    },
    getSearchableFieldValue(item, field) {
      if (!item || !field || field === "all") return this.toSearchText(item)

      const direct = this.unwrap(item?.[field])

      if (direct != null && direct !== "") {
        return this.toSearchText(direct)
      }

      if (field === "__sub") {
        return this.toSearchText(
          item?.__sub ||
            item?.borrower_name ||
            item?.borrowername ||
            item?.borrowers ||
            ""
        )
      }

      if (field === "__key") {
        return this.toSearchText(
          item?.__key ||
            item?.loanNumber ||
            item?.loannumber ||
            item?.f364 ||
            item?.appid ||
            item?.id ||
            ""
        )
      }

      if (field === "__title") {
        return this.toSearchText(
          item?.__title ||
            item?.title ||
            item?.tasktitle ||
            item?.taskname ||
            item?.name ||
            ""
        )
      }

      if (field === "__status") {
        return this.toSearchText(
          item?.__status ||
            item?.status ||
            item?.appstatus ||
            item?.msstatus ||
            ""
        )
      }

      if (field === "__assignedTo") {
        return this.toSearchText(
          item?.__assignedTo ||
            item?.assignedto ||
            item?.loanofficername ||
            item?.loanofficerid ||
            ""
        )
      }

      if (field === "__date") {
        return this.toSearchText(
          item?.__date ||
            item?.created ||
            item?.datecreated ||
            item?.createddate ||
            item?.dateCreated ||
            item?.appdate ||
            item?.logmsdatestarted ||
            item?.cust17fv ||
            ""
        )
      }

      if (field === "__followUpDate") {
        return this.toSearchText(
          item?.__followUpDate ||
            item?.followupdate ||
            ""
        )
      }

      if (field === "__type") {
        return this.toSearchText(item?.__type || this.typeLabel(item?.__type || ""))
      }

      return this.toSearchText(item?.[field])
    },
    toggleConfirmDialog() {
      this.showConfirmDialog = !this.showConfirmDialog

      if (!this.showConfirmDialog) {
        this.confirmMsg = ""
        this.confirmNote = ""
        this.confirmAction = ""
        this.singleNotifyTask = null
      }
    },
    toggleErrorDialog() {
      this.showErrorDialog = !this.showErrorDialog

      if (!this.showErrorDialog) {
        this.errorTitle = ""
        this.errorMsg = ""
      }
    },
    closeErrorDialog() {
      // Work Item #9107: the shared ErrorDialog does not declare its "close" emit, so Vue also passes our
      // @close listener through to the inner Kendo dialog and one click on X fires it twice. A toggle
      // re-opened the dialog empty (no title bar or X), so closing must be safe to call more than once.
      this.showErrorDialog = false
      this.errorTitle = ""
      this.errorMsg = ""
    },
    async onActionConfirmed() {
      const action = this.confirmAction
      this.toggleConfirmDialog()

      if (action === "single") {
        await this.sendSingleTaskNotification()
        return
      }

      await this.sendBulkTaskNotifications()
    },
    toggleRowSelected(item) {
      if (!item) return

      const id = item.id || item.task_id || item.__rowId
      if (!id) return

      const next = new Set(this.selectedIds)

      if (next.has(id)) next.delete(id)
      else next.add(id)

      this.selectedIds = next
      this.$emit("selection-change", { id, selected: next.has(id), item })
    },
    toggleSelectAllList() {
      const rows = this.viewMode === "list" ? this.pagedListData : this.filteredGridData
      const makeSelected = !this.allSelectedInList
      const next = new Set(this.selectedIds)

      ;(rows || []).forEach((r) => {
        const id = r.id || r.task_id || r.__rowId
        if (!id) return

        if (makeSelected) next.add(id)
        else next.delete(id)
      })

      this.selectedIds = next
      this.$emit("header-selection-change", { selected: makeSelected, mode: this.viewMode })
    },
    toggleSelectAllGrid() {
      const makeSelected = !(this.filteredGridData || []).every((r) => !!r[this.selectedField])
      const next = new Set(this.selectedIds)

      ;(this.filteredGridData || []).forEach((r) => {
        const id = r.id || r.task_id || r.__rowId
        if (!id) return

        if (makeSelected) next.add(id)
        else next.delete(id)
      })

      this.selectedIds = next
      this.$emit("header-selection-change", { selected: makeSelected, mode: "grid" })
    },
    onSendTaskNotification() {
      if (!this.isBorrowerTaskDialog || this.sending) return

      if (!this.filteredTaskIdsToSend.length) {
        this.errorTitle = "No Tasks Selected"
        this.errorMsg = "Select at least one borrower task to notify."
        this.showErrorDialog = true
        return
      }

      this.confirmAction = "bulk"
      this.confirmMsg = "Send notification to the borrower(s) for the selected task(s)?"
      this.confirmNote = "Borrowers must have a valid email on file to receive the notification."
      this.showConfirmDialog = true
    },
    async sendBulkTaskNotifications() {
      if (this.sending) return

      const { signingLoanIds, taskLoanIds, loanNumbers } = this.loansToNotify
      if (!signingLoanIds.length && !taskLoanIds.length) return

      this.sending = true
      let sentCount = 0
      const failedLoanIds = []
      const failures = []

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        if (!tokenResponse?.accessToken) throw new Error("Failed to acquire token")
        const token = tokenResponse.accessToken

        const sends = [
          ...taskLoanIds.map((loanId) => ({ loanId, send: () => apiService.CreateManualBorrowerTaskNotifications(loanId, token) })),
          ...signingLoanIds.map((loanId) => ({ loanId, send: () => apiService.CreateManualSigningTaskNotifications(loanId, token) }))
        ]

        // One failed loan no longer stops the rest; each result is reported.
        for (const item of sends) {
          try {
            await item.send()
            sentCount++
          } catch (error) {
            failedLoanIds.push(item.loanId)
            failures.push(`${loanNumbers[item.loanId] || "Loan"}: ${this.getSendErrorMessage(error)}`)
          }
        }
      } catch (error) {
        this.errorTitle = "Failed to Send Notifications"
        this.errorMsg = this.getSendErrorMessage(error)
        this.showErrorDialog = true
        return
      } finally {
        this.sending = false
      }

      // Keep only failed loans selected so a retry does not re-send the ones that went through.
      this.selectedIds = new Set(failedLoanIds)

      if (!failures.length) {
        const message = "Notification(s) sent successfully"
        this.showSendToast(message)
        this.$emit("notification-success", message)
        return
      }

      this.errorTitle = sentCount ? "Some Notifications Were Not Sent" : "Failed to Send Notifications"
      this.errorMsg = `${sentCount} sent, ${failures.length} not sent. ${failures.join(" | ")}`
      this.showErrorDialog = true
    },
    getSendErrorMessage(error) {
      const data = error?.response?.data
      return data?.message || data?.Message || data?.data || error?.message || "Unknown error"
    },
    showSendToast(message, type = "success") {
      clearTimeout(this.sendToastTimer)
      this.sendToast = { show: true, message, type }
      // Same 3-second display as the Command Center dashboards' showToast().
      this.sendToastTimer = setTimeout(() => this.hideSendToast(), 3000)
    },
    hideSendToast() {
      clearTimeout(this.sendToastTimer)
      this.sendToast = { ...this.sendToast, show: false }
    },
    async sendSingleTaskNotification() {
      const task = this.singleNotifyTask
      const id = task?.id || task?.task_id || task?.__rowId

      if (!id) {
        this.errorTitle = "Unable to Send Notification"
        this.errorMsg = "No valid borrower task was provided."
        this.showErrorDialog = true
        return
      }

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        if (!tokenResponse?.accessToken) throw new Error("Failed to acquire token")

        await apiService.CreateManualBorrowerTaskNotifications(id, tokenResponse.accessToken)
        this.$emit("notification-success", "Notification sent successfully")
      } catch (error) {
        this.errorTitle = "Failed to Send Notification"
        this.errorMsg = error?.response?.data?.data || error?.message || "Unknown error"
        this.showErrorDialog = true
      }
    },
   
onListRowClick(payload) {
  console.log("row payload", payload)
  const dataItem = payload?.dataItem ?? payload
  console.log("tasklink:", dataItem.tasklink)
  this.$emit("row-click", { dataItem })
},
    onHandleSort(sort) {
      if (!sort || !sort.field) return

      this.dataState = {
        ...this.dataState,
        sort: [{ field: sort.field, dir: sort.dir }]
      }
    },
    onDialogClose() {
      this.$emit("close")
    },
    close() {
      this.searchWord = ""
      this.searchField = "all"
      this.advFilters = DEFAULT_ADV_FILTERS()
      this.localData = this.originalData.map((x) => ({ ...x }))
      this.dataState = { ...this.dataState, skip: 0 }
      this.listPage = { ...this.listPage, skip: 0 }
      this.selectedIds = new Set()
      this.expandedCardIds = new Set()
      this.singleNotifyTask = null
      this.$emit("close")
    },
    onSearchInput(val) {
      this.searchWord = val
      this.applySearch()
    },
    onSearchFieldChange(val) {
      this.searchField = val || "all"
      this.applySearch()
    },
    applySearch() {
      const keyword = (this.searchWord || "").toLowerCase().trim()

      if (!keyword) {
        const base = this.originalData.map((x) => ({ ...x }))
        this.localData = this.enableFilters ? this.applyAdvancedFilters(base) : base
        this.dataState = { ...this.dataState, skip: 0 }
        this.listPage = { ...this.listPage, skip: 0 }
        this.expandedCardIds = new Set()
        return
      }

      let sourceRows =
        this.viewMode === "grid"
          ? this.originalGridData || []
          : this.originalData || []

      if (this.enableFilters) sourceRows = this.applyAdvancedFilters(sourceRows)

      const filtered = sourceRows
        .filter((item) => {
          const haystack = this.getSearchableFieldValue(item, this.searchField)
          return haystack.includes(keyword)
        })
        .map((x) => ({ ...x }))

      this.localData = filtered
      this.dataState = { ...this.dataState, skip: 0 }
      this.listPage = { ...this.listPage, skip: 0 }

      const nextExpanded = new Set()
      filtered.forEach((r, idx) => {
        const id = r.id || r.task_id || r.__rowId || `${idx}`
        r.__rowId = id
        nextExpanded.add(id)
      })
      this.expandedCardIds = nextExpanded

      this.$nextTick(() => {
        this.clampListPage()
      })
    },
    clearSearch() {
      this.searchWord = ""
      const base = this.originalData.map((x) => ({ ...x }))
      this.localData = this.enableFilters ? this.applyAdvancedFilters(base) : base
      this.dataState = { ...this.dataState, skip: 0 }
      this.listPage = { ...this.listPage, skip: 0 }
      this.expandedCardIds = new Set()
    },
    parseDateValue(v) {
      if (!v) return ""

      const un = this.unwrap(v)

      if (typeof un === "string" || typeof un === "number") return un

      if (un && typeof un === "object") {
        if (typeof un.value === "string" || typeof un.value === "number") return un.value
        if (typeof un.displayText === "string") return un.displayText
        if (un._custom && (un._custom.value || un._custom.displayText)) {
          return un._custom.value || un._custom.displayText
        }
      }

      return ""
    },
    formatDate(val) {
      if (!val) return ""
      const s = String(val).trim()
      const d = new Date(s)
      if (isNaN(d.getTime())) return s

      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric"
      })
    },
    formatShortDate(val) {
      if (!val) return "—"
      const d = new Date(val)
      if (isNaN(d.getTime())) return val || "—"

      return d.toLocaleDateString(undefined, {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      })
    },
    buildNestedExcelRows(rows) {
      const out = []

      ;(rows || []).forEach((r) => {
        const group = this.unwrap(r) || r

        const loanNumber =
          group?.loanNumber ||
          group?.loannumber ||
          group?.f364 ||
          group?.__key ||
          ""

        const borrowers = Array.isArray(group?.borrowers)
          ? group.borrowers.join(", ")
          : group?.borrowername || group?.borrower_name || group?.__sub || ""

        const tasks = Array.isArray(group?.tasks) ? group.tasks : []

        if (tasks.length) {
          tasks.forEach((t) => {
            const tv = this.unwrap(t) || t

            const created = this.formatDate(this.parseDateValue(tv?.created))
            const followUp = this.formatDate(this.parseDateValue(tv?.followupdate))

            out.push({
              loanNumber: tv?.loannumber || loanNumber,
              borrowers: tv?.borrowername || borrowers,
              taskTitle: tv?.title || "",
              status: tv?.status || "",
              assignedTo: tv?.assignedto || "",
              created,
              followUpDate: followUp,
              notes: tv?.notes || "",
              lastUpdated: tv?.lastupdated || "",
              taskLink: tv?.tasklink || ""
            })
          })

          return
        }

        out.push({
          loanNumber: loanNumber || "",
          borrowers: borrowers || "",
          taskTitle: group?.__title || "",
          status: group?.__status || "",
          assignedTo: group?.__assignedTo || "",
          created: this.formatDate(group?.__date) || "",
          followUpDate: this.formatDate(group?.__followUpDate) || "",
          notes: "",
          lastUpdated: "",
          taskLink: ""
        })
      })

      return out
    },
    exportExcel() {
      const rows = this.viewMode === "grid" ? this.gridData || [] : this.visibleRows || []

      const hasNestedTasks =
        this.viewMode !== "grid" &&
        rows.some((r) => {
          const g = this.unwrap(r) || r
          return Array.isArray(g?.tasks) && g.tasks.length
        })

      if (hasNestedTasks) {
        const dataToExport = this.buildNestedExcelRows(rows)

        const excelColumns = [
          { field: "loanNumber", title: "Loan Number", width: 160 },
          { field: "borrowers", title: "Borrowers", width: 220 },
          { field: "taskTitle", title: "Task", width: 260 },
          { field: "status", title: "Status", width: 140 },
          { field: "assignedTo", title: "Assigned To", width: 180 },
          { field: "created", title: "Created", width: 140 },
          { field: "followUpDate", title: "Follow Up Date", width: 160 },
          { field: "notes", title: "Notes", width: 320 },
          { field: "lastUpdated", title: "Last Updated", width: 220 },
          { field: "taskLink", title: "Task Link", width: 360 }
        ]

        const safeName = (this.title || "Export").replace(/[\\/:*?"<>|]/g, "-")

        saveExcel({
          data: dataToExport,
          fileName: `${safeName}.xlsx`,
          columns: excelColumns
        })

        return
      }

      const colsSource =
        Array.isArray(this.columns) && this.columns.length
          ? this.columns
          : this.effectiveColumns

      const excelColumns = (colsSource || [])
        .filter((c) => c && c.field && c.field !== "actions")
        .map((c) => ({
          field: c.field,
          title: c.title || c.field,
          width: c.width,
          format: c.format || undefined
        }))

      const dataToExport = rows.map((r) => {
        const row = {}
        excelColumns.forEach((c) => {
          if (c.field === "__date" || c.field === "__followUpDate") {
            row[c.field] = this.formatDate(r[c.field])
          } else {
            row[c.field] = r[c.field]
          }
        })
        return row
      })

      const safeName = (this.title || "Export").replace(/[\\/:*?"<>|]/g, "-")

      saveExcel({
        data: dataToExport,
        fileName: `${safeName}.xlsx`,
        columns: excelColumns
      })
    },
    clean(v) {
      if (v == null) return ""
      const s = String(v).trim()
      if (!s) return ""

      const low = s.toLowerCase()
      if (low === "undefined" || low === "null") return ""

      return s
    },
    parseCreated(v) {
      if (!v) return ""
      if (typeof v === "string" || typeof v === "number") return v
      if (v._custom?.value) return v._custom.value
      if (v.value) return v.value
      if (v.displayText) return v.displayText
      return ""
    },
    normalizeRow(item, forcedType) {
      const type = forcedType || "unknown"

      const out = {
        ...item,
        __type: type,
        __title: "",
        __sub: "",
        __key: "",
        __status: "",
        __date: "",
        __followUpDate: "",
        __assignedTo: ""
      }

     if (type === "borrowerTask" || type === "borrowerTaskGrouped") {
  const createdRaw = this.parseCreated(
    item.created || item.datecreated || item.createddate || item.dateCreated
  )
  out.__title = this.clean(item.title || item.taskname || item.tasktitle) || "Borrower Task"
  out.__sub = this.clean(item.borrower_name || item.borrowername) || "—"
  out.__key = this.clean(item.f364 || item.loannumber)
  out.__status = this.clean(item.status) || "Open"
  out.__date = createdRaw || ""
  out.__assignedTo = this.clean(item.assignedto)
  out.tasklink = item.tasklink || null
  out.id = item.id || null
  out.task_id = item.task_id || null
  out.appid = item.appid || null
  return out
}

      if (type === "internalTask") {
        const createdRaw = this.parseCreated(
          item.created || item.datecreated || item.createddate || item.dateCreated
        )
        out.__title = this.clean(item.title || item.taskname || item.tasktitle) || "Internal Task"
        out.__sub = this.clean(item.borrowername || item.borrower_name) || "—"
        out.__key = this.clean(item.loannumber || item.f364)
        out.__status = this.clean(item.status) || "—"
        out.__date = createdRaw || ""
        out.__followUpDate = this.clean(item.followupdate) || ""
        out.__assignedTo = this.clean(item.assignedto)
        return out
      }

      if (type === "application") {
        out.__title = "Application"
        out.__sub = this.clean(item.borrowername || item.borrower_name) || "—"
        out.__key = this.clean(item.appid || item.id)
        out.__status = this.clean(item.status || item.appstatus) || "App"
        out.__date = item.appdate || item.logmsdatestarted || ""
        out.__assignedTo = this.clean(item.loanofficername || item.loanofficerid)
        return out
      }

      if (type === "loan") {
        out.__title = this.clean(item.name) || "Loan"
        out.__sub = this.clean(item.f364 || item.loannumber) || "—"
        out.__key = this.clean(item.f364 || item.loannumber)
        out.__status = this.clean(item.msstatus) || "—"
        out.__date = item.cust17fv || ""
        out.__assignedTo = this.clean(
          item.loanofficername || item.loanofficerid || item.assignedto
        )
        return out
      }

      out.__title = this.clean(item.title || item.name) || "Item"
      out.__sub = this.clean(item.borrowername || item.borrower_name) || "—"
      out.__key = this.clean(item.f364 || item.loannumber || item.appid || item.id) || ""
      out.__status = this.clean(item.status || item.msstatus) || "—"
      out.__date = ""
      out.__assignedTo = this.clean(item.assignedto)
      return out
    },
    typeLabel(t) {
      if (t === "borrowerTask") return "Borrower Task"
      if (t === "internalTask") return "Internal Task"
      if (t === "application") return "Application"
      if (t === "loan") return "Loan"
      return "Item"
    },
    onSingleNotifyRequested(task) {
      this.singleNotifyTask = task
      this.confirmAction = "single"
      this.confirmMsg = "Send notification to the borrower for this task?"
      this.confirmNote = "Borrower must have a valid email on file to receive the notification."
      this.showConfirmDialog = true
    }
  }
}
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.confirm-dialog {
  z-index: 09999999999999999;
}

/* Work Item #9107: same toast as the Command Center dashboards (LoanOfficerDashboard, ProcessorDashboard,
   LoanOfficerAssistantDashboard, CommandCenter). Their .cc-toast rules are scoped to those components,
   so they are repeated here; z-index keeps it above the dialog overlay. */
.cc-toast {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  pointer-events: none;
  white-space: nowrap;
}
.cc-toast--success { background: #e9f7ef; color: #1e7f4f; border: 1px solid #d2f0df; }
.cc-toast--error   { background: #fdebec; color: #9f2d2f; border: 1px solid #f8d1d3; }
.cc-toast-enter-active, .cc-toast-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.cc-toast-enter-from, .cc-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }

.ccgd-expand-pref-row {
  display: flex;
  justify-content: flex-end;
  margin: 4px 0 8px;
}

.ccgd-expand-pref {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
  user-select: none;
}

.ccgd-expand-pref input {
  margin: 0;
}

.ccgd-grid-wrap {
  min-height: 0;
  flex: 1;
}

.ccgd-grid-title {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ccgd-grid-title-primary {
  font-weight: 700;
  color: #24314f;
}

.ccgd-grid-title-sub {
  font-size: 12px;
  color: #7b8397;
}

.ccgd-grid-dim {
  color: #98a1b3;
}

.ccgd-grid-date,
.ccgd-grid-assigned {
  color: #24314f;
}

.ccgd-type-pill,
.ccgd-status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}

.ccgd-type-pill[data-type="borrowerTask"] {
  background: #edf4ff;
  border-color: #d7e5fd;
  color: #3d6fd1;
}

.ccgd-type-pill[data-type="internalTask"] {
  background: #f4f0ff;
  border-color: #e1d8ff;
  color: #6d4fd1;
}

.ccgd-type-pill[data-type="loan"] {
  background: #eef9f0;
  border-color: #d9eedf;
  color: #2c7a4b;
}

.ccgd-type-pill[data-type="application"] {
  background: #fff7e7;
  border-color: #f5e3b5;
  color: #9a6b11;
}

.ccgd-status-pill[data-status="complete"],
.ccgd-status-pill[data-status="completed"] {
  background: #eef9f0;
  border-color: #d9eedf;
  color: #2c7a4b;
}

.ccgd-status-pill[data-status="pending"] {
  background: #f2f4f7;
  border-color: #e1e5ec;
  color: #6d7789;
}

.ccgd-status-pill[data-status="in progress"] {
  background: #edf4ff;
  border-color: #d7e5fd;
  color: #3d6fd1;
}

.ccgd-status-pill[data-status="requested"] {
  background: #fff7e7;
  border-color: #f5e3b5;
  color: #9a6b11;
}

.ccgd-loading-state {
  min-height: 420px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: rgba(15, 23, 42, 0.68);
}

.ccgd-loading-state__icon {
  font-size: 28px;
}

.ccgd-loading-state__text {
  font-size: 14px;
  font-weight: 800;
}

.ccgd-list-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 2px 0;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  margin-top: 10px;
  flex-wrap: wrap;
}

.ccgd-list-pager__left,
.ccgd-list-pager__right {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.ccgd-list-pager__summary,
.ccgd-list-pager__page,
.ccgd-list-pager__size span {
  font-size: 12px;
  font-weight: 800;
  color: #5b657a;
}

.ccgd-list-pager__size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ccgd-list-pager__size select {
  min-width: 72px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 800;
  color: #24314f;
}

.ccgd-page-btn {
  height: 32px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  color: #24314f;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.ccgd-page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ccgd-page-btn:not(:disabled):hover {
  background: #f8fafc;
}

.cgsdark .ccgd-list-pager {
  border-top-color: rgba(148, 163, 184, 0.14);
}

.cgsdark .ccgd-list-pager__summary,
.cgsdark .ccgd-list-pager__page,
.cgsdark .ccgd-list-pager__size span {
  color: rgba(226, 232, 240, 0.78);
}

.cgsdark .ccgd-list-pager__size select,
.cgsdark .ccgd-page-btn {
  background: rgba(15, 23, 42, 0.78);
  border-color: rgba(148, 163, 184, 0.16);
  color: rgba(226, 232, 240, 0.92);
}
</style>
